import React, { useState, useContext, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ref, set } from 'firebase/database';
import { database } from '../config/firebase';
import { AuthContext } from '../contexts/AuthContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import activityService from '../utils/activityService';
import UpgradeToProModal from './UpgradeToProModal';
import { subscribePlanCounts, canCreateOrJoinTeam } from '../utils/planGate';

interface MemberField {
  email: string;
  role: 'admin' | 'editor' | 'viewer';
}

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ isOpen, onClose }) => {
  const authContext = useContext(AuthContext);
  const currentUid = authContext?.user?.uid;
  const currentEmail = authContext?.user?.email;

  const [teamName, setTeamName] = useState('');
  const [teamDesc, setTeamDesc] = useState('');
  const [members, setMembers] = useState<MemberField[]>([{ email: '', role: 'admin' }]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [teamCount, setTeamCount] = useState(0);

  // Keep team count updated for gating
  useEffect(() => {
    if (!currentUid) return;
    const unsub = subscribePlanCounts(currentUid, (c) => setTeamCount(c.teamCount));
    return () => { if (unsub) unsub(); };
  }, [currentUid]);

  if (!isOpen) return null;

  const handleMemberChange = (idx: number, field: keyof MemberField, value: string) => {
    setMembers((prev) => prev.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));
  };

  const addMemberField = () => setMembers((prev) => [...prev, { email: '', role: 'viewer' }]);
  const removeMemberField = (idx: number) => setMembers((prev) => prev.filter((_, i) => i !== idx));

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUid || !currentEmail) {
      setError('User not authenticated');
      return;
    }
    // Free plan limit: 3 teams
    if (!canCreateOrJoinTeam(teamCount)) {
      setShowUpgrade(true);
      return;
    }
    setCreating(true);
    setError('');
    try {
      const teamId = uuidv4();
      const teamRef = ref(database, `teams/${teamId}`);
      const teamMembers: { [uid: string]: MemberField } = {
        [currentUid]: { email: currentEmail, role: 'admin' },
      };
      // Write team object
      await set(teamRef, {
        id: teamId,
        name: teamName,
        description: teamDesc,
        members: teamMembers,
        created_by: currentUid,
        created_at: Date.now(),
      });
      // Create pending invites for each additional member
      for (const m of members) {
        if (m.email && m.email !== currentEmail) {
          const inviteId = uuidv4();
          const inviteRef = ref(database, `invites/${inviteId}`);
          await set(inviteRef, {
            email: m.email,
            role: m.role,
            teamId,
            teamName: teamName,
            inviter: currentEmail,
            status: 'pending',
            createdAt: Date.now(),
          });
        }
      }
      // Log activity for team creation
      await activityService.logTeamJoined(currentUid, teamId, teamName);
      
      // Reset local state and close modal
      setTeamName('');
      setTeamDesc('');
      setMembers([{ email: '', role: 'admin' }]);
      onClose();
    } catch (err) {
      console.error('Create team error', err);
      setError('Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-lg p-8 w-full max-w-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" onClick={onClose}>
          <XMarkIcon className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold mb-4">Create New Team</h2>
        <form onSubmit={handleCreateTeam} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-1">Team Name</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-1">Description</label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={teamDesc}
              onChange={(e) => setTeamDesc(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-1">Invite Members</label>
            {members.map((member, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <input
                  type="email"
                  placeholder="Email"
                  className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={member.email}
                  onChange={(e) => handleMemberChange(idx, 'email', e.target.value)}
                  required={idx === 0}
                />
                <select
                  className="px-2 py-2 border rounded-lg"
                  value={member.role}
                  onChange={(e) => handleMemberChange(idx, 'role', e.target.value as MemberField['role'])}
                >
                  {roleOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {members.length > 1 && (
                  <button type="button" className="text-red-500 text-lg px-2" onClick={() => removeMemberField(idx)}>
                    &times;
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="text-blue-600 hover:underline text-sm mt-1"
              onClick={addMemberField}
            >
              + Add another member
            </button>
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 rounded-lg font-semibold shadow hover:from-blue-600 hover:to-purple-600 transition-all disabled:opacity-50"
            disabled={creating}
          >
            {creating ? 'Creating...' : 'Create Team'}
          </button>
        </form>
      </div>
    </div>
    {showUpgrade && (
      <UpgradeToProModal isOpen={true} onClose={() => setShowUpgrade(false)} context="team" />
    )}
    </>
  );
};

export default CreateTeamModal; 